import React from 'react'
import {
  MetadataList,
  MetadataListItem,
  metadataListItemCss,
  metadataLabelCss
} from '../../legacy-design-system/metadata'
import { Status, Substatus } from '../vulnerability-status'
import { FormattedMessage } from 'react-intl'
import { WithTooltip, tooltipTitleCss } from '../../design-system'
import { listResetCss } from '../../legacy-design-system/list'
import { css, cx } from '@emotion/css'
import {
  VulnerabilitySeverityBadge,
  Severity,
  VulnerabilitySeverityChangeDropdown
} from '../vulnerability-severity'
import { metadataCss } from '../../legacy-design-system/text'
import { theme } from '../../styled'
import { PureQueryOptions } from 'apollo-client'
import { Role, RoleSwitch } from '../roles'

export interface VulnerabilityDashboardProps {
  readonly organizationID: string
  readonly status: Status
  readonly substatus?: Substatus
  readonly firstDetected: string
  readonly vulnerabilityID: string
  readonly role?: Role
  readonly likelihood: Severity
  readonly impact: Severity
  readonly severity: Severity
  readonly defaultSeverity?: Severity
  readonly refresh?: () => Promise<any>
  readonly refetchQueries?: PureQueryOptions[]
  readonly pendingStatus?: Status
}

function Separator() {
  return (
    <span
      className={css`
        margin-right: 5px;
      `}
    >
      :
    </span>
  )
}

function displaySubstatus(substatus: Substatus) {
  const styles = css`
    padding: 0 5px;
  `

  if (substatus.alias) {
    return (
      <span className={metadataCss}>
        <span className={styles}>-</span>
        <span>{substatus.alias}</span>
      </span>
    )
  }
  return (
    <FormattedMessage id={substatus.i18nIDShort}>
      {(msg) => (
        <span className={metadataCss}>
          <span className={styles}>-</span>
          <span>{msg}</span>
        </span>
      )}
    </FormattedMessage>
  )
}

function VulnerabilitySeverityViewOnly({
  severity,
  likelihood,
  impact
}: {
  readonly severity: Severity
  readonly likelihood: Severity
  readonly impact: Severity
}) {
  return (
    <WithTooltip
      renderTooltipContent={() => {
        return (
          <ul className={listResetCss}>
            <li>
              <FormattedMessage
                id="title_likelihood"
                children={(title) => (
                  <span className={tooltipTitleCss}>{title}</span>
                )}
              />
              <Separator />
              <FormattedMessage id={likelihood.i18nID} />
            </li>
            <li>
              <FormattedMessage
                id="title_impact"
                children={(title) => (
                  <span className={tooltipTitleCss}>{title}</span>
                )}
              />
              <Separator />
              <FormattedMessage id={impact.i18nID} />
            </li>
          </ul>
        )
      }}
      children={() => <VulnerabilitySeverityBadge severity={severity} />}
    />
  )
}

export function VulnerabilityDashboard({
  organizationID,
  defaultSeverity,
  likelihood,
  impact,
  status,
  substatus,
  firstDetected,
  role,
  vulnerabilityID,
  severity,
  pendingStatus,
  refetchQueries = [],
  refresh
}: VulnerabilityDashboardProps) {
  const visibleStatus = pendingStatus || status
  return (
    <MetadataList>
      <li className={metadataListItemCss}>
        {!!role && defaultSeverity && refresh ? (
          <RoleSwitch
            role={role}
            requiredRole={'RULES_ADMIN'}
            renderAllowed={() => (
              <VulnerabilitySeverityChangeDropdown
                organizationID={organizationID}
                vulnerabilityID={vulnerabilityID}
                defaultSeverity={defaultSeverity}
                severity={severity}
                refresh={refresh}
                refetchQueries={refetchQueries}
              />
            )}
            renderDenied={() => (
              <VulnerabilitySeverityViewOnly
                severity={severity}
                likelihood={likelihood}
                impact={impact}
              />
            )}
          />
        ) : (
          <VulnerabilitySeverityViewOnly
            severity={severity}
            likelihood={likelihood}
            impact={impact}
          />
        )}
      </li>

      <FormattedMessage id="title_date">
        {(text) => <MetadataListItem label={text} value={firstDetected} />}
      </FormattedMessage>

      <li className={metadataListItemCss}>
        <FormattedMessage
          id="title_status"
          children={(text) => (
            <label className={metadataLabelCss}>{text}:</label>
          )}
        />
        <FormattedMessage
          id={visibleStatus.i18nID}
          children={(msg) => (
            <span
              className={metadataCss}
              children={msg}
              data-e2e="vulnerability-status"
            />
          )}
        />
        {substatus && displaySubstatus(substatus)}
        {pendingStatus && (
          <FormattedMessage
            id="title_pending"
            children={(msg) => (
              <strong
                className={cx(
                  metadataCss,
                  css`
                    font-weight: ${theme.fontWeights.secondary.bold};
                    margin-left: 3px;
                  `
                )}
              >
                ({msg})
              </strong>
            )}
          />
        )}
      </li>

      <FormattedMessage id="title_identifier">
        {(text) => (
          <MetadataListItem
            label={text}
            value={vulnerabilityID}
            e2eTestid="vulnerability-id"
          />
        )}
      </FormattedMessage>
    </MetadataList>
  )
}
